import type {ReactAction} from '@netx/core/bootstrap';
import {i18nLocaleCompare} from '../../locales/utils';

/**
 * Provided a list of actions, this function will return an array of actions
 * that are ordered by their `order` property. If `disableSort` is true, the
 * actions will not be sorted by their `label`.
 * @param actions Actions
 * @param disableSort Disable sort
 * @returns Ordered actions
 *
 * @example
 * ```ts
 * const orderedActions = actionsToOrderedActions(actions);
 * ```
 */
export const actionsToOrderedActions = (
	actions: ReactAction[],
	disableSort?: boolean,
): Array<ReactAction | ReactAction[]> => {
	const groups = actions
		// Group actions by their `group` property
		.reduce<ReactAction[][]>((groups, action) => {
			const group = Math.max(action.group ?? 0, 0);
			// Create a new group if it does not exist
			const actionGroup = (groups[group] = groups[group] ?? []);
			// Add the action to the group
			actionGroup.push(action);
			return groups;
		}, [])
		// Remove empty groups
		.filter(Boolean)
		// Sort actions within each group
		.map((actionGroup) => {
			// if `disableSort` is true, do not sort the actions by their `label`
			if (!disableSort) {
				actionGroup.sort((a, b) => i18nLocaleCompare(a.label, b.label));
			}

			// Split the actions into two arrays: ordered and unordered
			const {ordered, unordered} = actionGroup.reduce(
				({ordered, unordered}, action) => ({
					ordered: action.order != null ? [...ordered, action] : ordered,
					unordered: action.order == null ? [...unordered, action] : unordered,
				}),
				{
					ordered: [] as ReactAction[],
					unordered: [] as ReactAction[],
				},
			);

			// Sort the ordered actions by their `order` property
			// and return the ordered actions followed by the unordered actions
			return [
				...ordered.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
				...unordered,
			];
		});

	// If there is only one group, unwrap and simply return the group
	return groups.length === 1 ? groups[0] : groups;
};
